/**
 * GP Popups Admin Bar Entry Point
 *
 * Initializes Alpine.js components for the admin bar debugging panel.
 * This bundle is separate from the main frontend bundle to avoid conflicts.
 *
 * IMPORTANT: This script uses Alpine.prefix('x-gpp-') to create an isolated
 * Alpine.js instance that won't conflict with themes that also use Alpine.js.
 * All Alpine directives in the admin bar templates use the x-gpp- prefix
 * (e.g., x-gpp-data, x-gpp-show, x-gpp-on:click instead of x-data, x-show, @click).
 */

import Alpine from 'alpinejs';
import { createAdminBarStore, type AdminBarStore } from './store';
import { createAdminBarComponent } from './components';
import { evaluateAllFeeds } from './targeting-evaluator';
import {
	TRIGGER_STATE_EVENT,
	POPUP_STATE_EVENT,
	type TriggerState,
	type PopupState,
} from '../frontend/triggers/trigger-state-broadcaster';
import './styles/admin-bar.css';

/**
 * Global API for controlling the debug panel programmatically.
 * Useful for demo pages or custom integrations.
 */
export interface GPPopupsDebugAPI {
	/** Open the debug panel */
	open: () => void;
	/** Close the debug panel */
	close: () => void;
	/** Toggle the debug panel */
	toggle: () => void;
	/** Check if the panel is currently open */
	isOpen: () => boolean;
	/** Initialize Alpine on an element (for custom toggle buttons) */
	initElement: (element: HTMLElement) => void;
}

// Declare global config type
declare global {
	interface Window {
		gpPopupsAdminBarConfig?: AdminBarConfig;
		gpPopupsDebug?: GPPopupsDebugAPI;
	}
}

export interface AdminBarConfig {
	feeds: AdminBarFeed[];
	nonce: string;
	editBaseUrl: string;
	/** Whether the current user is an admin (has manage_options capability) */
	isAdmin: boolean;
	/** Whether the panel should be open by default (filterable via PHP) */
	defaultOpen: boolean;
	/** Whether to persist panel state in localStorage (filterable via PHP) */
	persistState: boolean;
}

export interface AdminBarFeed {
	feedId: number;
	name: string;
	formId: number;
	isActive: boolean;
	editUrl: string;
	targetingGroups: import('../shared/types/targeting').TargetingGroup[];
	/** Cookie name used for dismissal tracking */
	cookieName: string;
	/** Whether this popup respects dismissal cookies */
	respectDismissal: boolean;
}

// Set custom prefix BEFORE registering any components or stores.
// This isolates our Alpine instance from any theme's Alpine instance.
// All HTML templates must use x-gpp-* directives instead of x-* directives.
Alpine.prefix('x-gpp-');

// Register store and component with our isolated Alpine instance
Alpine.store('gpPopupsAdminBar', createAdminBarStore());
Alpine.data('gpPopupsPanel', createAdminBarComponent);

/**
 * Helper to get the store instance.
 */
function getStore(): AdminBarStore {
	return Alpine.store('gpPopupsAdminBar') as AdminBarStore;
}

/**
 * Set up event listeners for trigger and popup state updates from the frontend.
 */
function setupStateListeners(store: AdminBarStore): void {
	// Listen for trigger state updates
	window.addEventListener(TRIGGER_STATE_EVENT, ((event: CustomEvent) => {
		const state = event.detail as TriggerState;
		store.updateTriggerState(state.feedId, state);
	}) as EventListener);

	// Listen for popup state updates
	window.addEventListener(POPUP_STATE_EVENT, ((event: CustomEvent) => {
		const state = event.detail as PopupState;
		store.updatePopupState(state.feedId, state.state);
	}) as EventListener);
}

/**
 * Create and expose the global API for controlling the debug panel.
 * This allows external code (like demo pages) to control the panel
 * without needing to access Alpine directly.
 */
function createGlobalAPI(): GPPopupsDebugAPI {
	return {
		open() {
			const store = getStore();
			if (!store.panelOpen) {
				store.togglePanel();
			}
		},
		close() {
			const store = getStore();
			if (store.panelOpen) {
				store.togglePanel();
			}
		},
		toggle() {
			getStore().togglePanel();
		},
		isOpen() {
			return getStore().panelOpen;
		},
		initElement(element: HTMLElement) {
			Alpine.initTree(element);
		},
	};
}

// Expose the global API immediately so it's available before DOMContentLoaded
window.gpPopupsDebug = createGlobalAPI();

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
	const config = window.gpPopupsAdminBarConfig;

	if (config) {
		// Initialize store with config
		const store = getStore();
		store.init(config);

		// Set up listeners for trigger/popup state updates
		setupStateListeners(store);

		// Evaluate targeting conditions and update store
		void evaluateAllFeeds(config.feeds, store);
	}

	// Initialize Alpine only on our debug panel element.
	// This prevents any interference with other Alpine instances.
	const panel = document.getElementById('gp-popups-debug-panel');
	if (panel) {
		Alpine.initTree(panel);
	}

	// Add click handler to admin bar trigger button
	const trigger = document.querySelector('#wp-admin-bar-gp-popups > a');
	if (trigger) {
		trigger.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			getStore().togglePanel();
		});
	}
});
